/**
 * fs.js — the playground's filesystem, such as it is.
 *
 * A flat path → text map, mirrored into the origin private file system. Flat
 * because rvmark's own addressing is flat: a file is named by a relative path,
 * directories exist only as the slashes inside those names, and nothing asks
 * for a directory's contents. The tree pane derives its shape from the names at
 * render time.
 *
 * A directory with no files in it has no name to live in, so it gets an entry
 * of its own, as in a zip: a name ending in a slash, with no text.
 *
 * The map is the truth while the page is open, and everything the editor needs
 * is answered from it synchronously. Each change is also sent to fs-worker.js,
 * which writes it to disk; the disk is read once, at load. Text is held here;
 * a file that is not text is held only as a mark, and its bytes stay on disk.
 */

/** Where the store lived before the file system, and is moved over from once. */
const STORE_KEY = 'rvmark-playground:fs';

/** Where the workspace lives under the origin's root: '' for the main one. Set by load. */
let BASE = '';

/** The directory under the base that holds the files. */
let ROOT = 'files';

/** Beside it, for files that are on their way out: a zip being built. */
let SCRATCH = 'scratch';

/** Beside it, for a rename under way; see fs-worker.js. */
let JOURNAL = 'journal';

const onDisk = (path) => ROOT + '/' + path;

/** Files carry no type of their own; anything without an extension gets rvmark's. */
export const EXT = '.rv.md';

export const isRvFile = (path) => path.endsWith(EXT) || path.endsWith('.rvmark');

/** The name the engine addresses a file by: `.rvmark` sources publish as `.rv.md`. */
export const publishedPath = (path) => path.endsWith('.rvmark') ? path.slice(0, -'.rvmark'.length) + EXT : path;

let files = new Map();

/** What the map holds for a file that is not text. */
const BINARY = Object.freeze({});

// ── The disk ──────────────────────────────────────────────────────────────────

/** Null when the browser has no file system to give: the map is then all there is. */
let worker = null;

let seq = 0;
const waiting = new Map();
let last = Promise.resolve();

let full = false;
let failed = false;
let onFull = null;

/** Call `fn` when storage stops taking writes. */
export const whenFull = (fn) => { onFull = fn; };

let onChange = null;

/** Call `fn` whenever the tree or a file's contents change. */
export const whenChanged = (fn) => { onChange = fn; };

function post(op, args = {}, transfer = []) {
  if (!worker) return;
  const id = ++seq;
  last = new Promise((resolve) => waiting.set(id, resolve));
  worker.postMessage({ id, op, ...args }, transfer);
}

/** Resolves once everything asked for so far is on disk. */
export const settled = () => last;

async function fileAt(path) {
  const parts = path.split('/');
  const name = parts.pop();
  let dir = await navigator.storage.getDirectory();
  for (const part of parts) dir = await dir.getDirectoryHandle(part);
  return (await dir.getFileHandle(name)).getFile();
}

/**
 * A file written piece by piece: `write(bytes)` as they come, then `close()`,
 * which answers with the finished file. With no disk the pieces are kept.
 */
function stream(path) {
  const kept = [];
  return {
    write(bytes) {
      // Copied: the caller's buffer may be one it goes on using.
      const own = bytes.slice();
      if (worker) post('append', { path, data: own }, [own.buffer]);
      else kept.push(own);
    },
    async close() {
      if (!worker) return new Blob(kept);
      post('close', { path });
      await last;
      return fileAt(path);
    },
  };
}

/** A file outside the tree, for building something to hand to the user. */
export function scratch(name) {
  post('remove', { path: SCRATCH });
  return stream(SCRATCH + '/' + name);
}

/** A file in the tree that arrives in pieces; its kind is settled on `close()`. */
export function sink(path) {
  const out = stream(onDisk(path));
  return {
    write: out.write,
    async close() {
      const blob = await out.close();
      const held = await classify(blob);
      files.set(path, held === BINARY && !worker ? blob : held);
      onChange?.();
    },
  };
}

function answered({ data: { id, error, detail } }) {
  waiting.get(id)?.();
  waiting.delete(id);
  if (!error) { full = false; return; }
  failed = true;
  console.warn('[playground] storage:', detail);
  if (error === 'QuotaExceededError' && !full) onFull?.();
  full = error === 'QuotaExceededError';
}

/** Text if the file is UTF-8 with no NUL in its head, else the mark for a binary. */
async function classify(blob) {
  const head = new Uint8Array(await blob.slice(0, 8192).arrayBuffer());
  if (head.includes(0)) return BINARY;
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(await blob.arrayBuffer());
  } catch (_) {
    return BINARY;
  }
}

async function walk(dir, prefix) {
  let empty = true;
  for await (const [name, handle] of dir.entries()) {
    empty = false;
    if (handle.kind === 'directory') await walk(handle, prefix + name + '/');
    else files.set(prefix + name, await classify(await handle.getFile()));
  }
  if (empty && prefix) files.set(prefix, '');
}

async function migrate() {
  let old;
  try {
    old = JSON.parse(localStorage.getItem(STORE_KEY) ?? 'null');
  } catch (_) {
    return;
  }
  if (!old) return;
  for (const [path, data] of Object.entries(old)) {
    await add(path, typeof data === 'string' ? data : Uint8Array.from(atob(data.b64), (c) => c.charCodeAt(0)));
  }
  await last;
  if (!failed) localStorage.removeItem(STORE_KEY);
}

/**
 * Read the disk into the map, for the workspace under `base` ('' or a path
 * ending in a slash). False if there is no disk and nothing will be kept.
 */
export async function load(base = '') {
  BASE = base;
  ROOT = base + 'files';
  SCRATCH = base + 'scratch';
  JOURNAL = base + 'journal';
  try {
    let root = await navigator.storage.getDirectory();
    for (const part of ROOT.split('/')) root = await root.getDirectoryHandle(part, { create: true });
    worker = new Worker('./fs-worker.js');
    worker.onmessage = answered;
    // A rename the last session did not finish is finished before anything is read.
    post('recover', { journal: JOURNAL });
    post('remove', { path: SCRATCH });
    await last;
    await walk(root, '');
  } catch (_) {
    worker = null;
    return false;
  }
  if (files.size === 0 && !BASE) await migrate();
  return true;
}

/** A small file kept beside the workspace's files, outside the tree; null if there is none. */
export async function beside(name) {
  try {
    await last;
    return await (await fileAt(BASE + name)).text();
  } catch (_) {
    return null;
  }
}

export function keepBeside(name, text) {
  post('write', { path: BASE + name, data: text });
}

// ── The map ───────────────────────────────────────────────────────────────────

export const paths   = () => [...files.keys()].filter((p) => !p.endsWith('/')).sort();
export const dirs    = () => [...files.keys()].filter((p) => p.endsWith('/')).sort();
export const has     = (p) => files.has(p);
export const isEmpty = () => files.size === 0;

export const isBinary = (p) => files.has(p) && typeof files.get(p) !== 'string';

/** A file's text; null if there is no such file or it is not text. */
export function read(p) {
  const held = files.get(p);
  return typeof held === 'string' ? held : null;
}

/** A file as a Blob, for the ones `read` has no text for. On disk, not in memory. */
export async function blob(path) {
  const held = files.get(path);
  if (held instanceof Blob) return held;
  await last;
  return fileAt(onDisk(path));
}

/** Set a file's text, or make a directory by its `dir/` key. */
export function write(path, text) {
  if (files.get(path) !== text) onChange?.();
  files.set(path, text);
  if (path.endsWith('/')) post('mkdir', { path: onDisk(path.slice(0, -1)) });
  else post('write', { path: onDisk(path), data: text });
}

/** Take in a file whose kind is not yet known: text, bytes, or a Blob. */
export async function add(path, data) {
  if (typeof data === 'string') { write(path, data); return; }
  const blob = data instanceof Blob ? data : new Blob([data]);
  const held = await classify(blob);
  // With no disk to read the bytes back from, the Blob itself is kept.
  files.set(path, held === BINARY && !worker ? blob : held);
  onChange?.();
  post('write', { path: onDisk(path), data: held === BINARY ? blob : held });
}

/** The `dir/` key a file or directory sits in; empty at the root. */
const parentKey = (key) => key.slice(0, key.lastIndexOf('/', key.length - 2) + 1);

/** A directory outlives its last file: once emptied it is kept as an entry. */
function keep(dir) {
  if (dir && covered(dir).length === 0) files.set(dir, '');
}

/** The entries a key names: a file's own path, or everything under a `dir/` key. */
export const covered = (key) =>
  [...files.keys()].filter((p) => key.endsWith('/') ? p.startsWith(key) : p === key);

/**
 * A file and a directory cannot share a name. The entry that stands in the way
 * of making `key`, or null.
 */
export function conflict(key) {
  const isDir = key.endsWith('/');
  const path  = isDir ? key.slice(0, -1) : key;
  if (isDir && files.has(path)) return path;
  if (!isDir && covered(key + '/').length) return key + '/';
  const parts = path.split('/');
  for (let i = 1; i < parts.length; i++) {
    const above = parts.slice(0, i).join('/');
    if (files.has(above)) return above;
  }
  return null;
}

/** Where `move` would send each file, old path → new, without doing it. */
export const moves = (from, to) => new Map(covered(from).map((p) => [p, to + p.slice(from.length)]));

/** Remove a file, or a directory by its `dir/` key. Returns the paths removed. */
export function remove(key) {
  const gone = covered(key);
  for (const p of gone) files.delete(p);
  keep(parentKey(key));
  onChange?.();
  post('remove', { path: onDisk(key.endsWith('/') ? key.slice(0, -1) : key) });
  return gone;
}

/**
 * Rename a file, or a directory by its `dir/` key. A file already at a target
 * is replaced. A directory does not move into itself.
 */
export function move(from, to) {
  const moved = moves(from, to);
  if (from === to || moved.size === 0) return;
  if (from.endsWith('/') && to.startsWith(from)) return;
  const taken = new Set(moved.values());
  // Rebuilt in iteration order so a rename keeps the file where it was rather
  // than sending it to the end.
  const next = new Map();
  for (const [p, held] of files) {
    if (moved.has(p)) next.set(moved.get(p), held);
    else if (!taken.has(p)) next.set(p, held);
  }
  files = next;
  keep(parentKey(from));
  onChange?.();

  const pairs = [...moved];
  post('rename', {
    journal: JOURNAL,
    moves:  pairs.filter(([old]) => !old.endsWith('/')).map((pair) => pair.map(onDisk)),
    dirs:   pairs.filter(([old]) => old.endsWith('/')).map(([, now]) => onDisk(now.slice(0, -1))),
    remove: from.endsWith('/') ? onDisk(from.slice(0, -1)) : null,
  });
}

/** Empty the tree, ahead of a wholesale replacement — what opening a zip does. */
export function clear() {
  files = new Map();
  onChange?.();
  post('remove', { path: ROOT });
}

/** Where a path's extension starts, `.rv.md` counting as one; its length if it has none. */
export function extStart(path) {
  const dot = path.endsWith(EXT) ? path.length - EXT.length : path.lastIndexOf('.');
  return dot > path.lastIndexOf('/') + 1 ? dot : path.length;
}

/**
 * A path that does not collide, by appending -1, -2, … before the extension.
 * Used for imports that would overwrite something.
 */
export function uniquePath(path) {
  if (!files.has(path)) return path;
  const dot = extStart(path);
  for (let n = 1; ; n++) {
    const candidate = `${path.slice(0, dot)}-${n}${path.slice(dot)}`;
    if (!files.has(candidate)) return candidate;
  }
}

/** Strip leading slashes and any `..` segment, so a zip cannot name its way out. */
export function normalizePath(path) {
  return path.split('/')
    .filter((seg) => seg && seg !== '.' && seg !== '..')
    .join('/');
}
